// src/pages/InformeAuditoria.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getAuditoria,
  getAuditorias,
} from "../helpers/rutaAuditoria";

/* ==================================================
   UTILIDADES
================================================== */

const todayLocalISO = () => {
  const d = new Date();

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();

  d.setDate(1);

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

const clean = (value) =>
  String(value ?? "").trim();

const formatDateTime = (value) => {
  if (!value) return "-";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "-";
  }

  return d.toLocaleString(
    "es-AR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }
  );
};

const formatDateTimePdf = (
  value
) => {
  if (!value) return "-";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "-";
  }

  return d.toLocaleString(
    "es-AR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

const parsearJson = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return null;
  }

  if (
    typeof value === "object"
  ) {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
};

const jsonBonito = (value) => {
  const parsed =
    parsearJson(value);

  if (parsed === null) {
    return "Sin información";
  }

  if (
    typeof parsed === "string"
  ) {
    return parsed;
  }

  return JSON.stringify(
    parsed,
    null,
    2
  );
};

const mostrarValor = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "-";
  }

  if (
    typeof value === "object"
  ) {
    return JSON.stringify(value);
  }

  return String(value);
};

const accionVariant = (
  accion
) => {
  const value =
    clean(accion).toUpperCase();

  if (
    value === "ALTA" ||
    value === "CREACION"
  ) {
    return "success";
  }

  if (
    value === "MODIFICACION" ||
    value === "ACTUALIZACION"
  ) {
    return "primary";
  }

  if (
    value === "ANULACION"
  ) {
    return "warning";
  }

  if (
    value === "ELIMINACION" ||
    value === "BAJA"
  ) {
    return "danger";
  }

  return "secondary";
};

const resultadoVariant = (
  resultado
) => {
  const value =
    clean(resultado)
      .toUpperCase();

  if (value === "OK") {
    return "success";
  }

  if (value === "ERROR") {
    return "danger";
  }

  return "secondary";
};

const rolTexto = (role) => {
  const value =
    clean(role)
      .toUpperCase();

  if (
    value === "ADMIN_ROLE"
  ) {
    return "ADMIN";
  }

  if (
    value === "USER_ROLE"
  ) {
    return "USUARIO";
  }

  if (
    value === "USER_CAM"
  ) {
    return "CAMIÓN";
  }

  if (
    value === "USER_PREV"
  ) {
    return "PREVISUALIZACIÓN";
  }

  return value || "-";
};

const escaparCSV = (value) => {
  const text =
    String(value ?? "");

  return `"${text.replace(
    /"/g,
    '""'
  )}"`;
};

/* ==================================================
   COMPONENTE
================================================== */

const InformeAuditoria = () => {
  /* ------------------------------
     FILTROS
  ------------------------------ */

  const [desde, setDesde] =
    useState(
      firstDayMonthISO()
    );

  const [hasta, setHasta] =
    useState(
      todayLocalISO()
    );

  const [
    usuario,
    setUsuario,
  ] = useState("");

  const [
    modulo,
    setModulo,
  ] = useState("");

  const [
    accion,
    setAccion,
  ] = useState("");

  const [
    entidad,
    setEntidad,
  ] = useState("");

  const [
    resultado,
    setResultado,
  ] = useState("");

  const [q, setQ] =
    useState("");

  /* ------------------------------
     DATOS
  ------------------------------ */

  const [
    auditorias,
    setAuditorias,
  ] = useState([]);

  const [
    usuariosDisponibles,
    setUsuariosDisponibles,
  ] = useState([]);

  const [
    resumen,
    setResumen,
  ] = useState({
    total: 0,
    altas: 0,
    modificaciones: 0,
    anulaciones: 0,
    eliminaciones: 0,
    errores: 0,
  });

  /* ------------------------------
     PAGINACIÓN
  ------------------------------ */

  const [total, setTotal] =
    useState(0);

  const [page, setPage] =
    useState(1);

  const [limit, setLimit] =
    useState(50);

  /* ------------------------------
     ESTADOS UI
  ------------------------------ */

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    exportando,
    setExportando,
  ] = useState(false);

  const [error, setError] =
    useState("");

  /* ------------------------------
     MODAL DETALLE
  ------------------------------ */

  const [
    modalVisible,
    setModalVisible,
  ] = useState(false);

  const [
    detalleLoading,
    setDetalleLoading,
  ] = useState(false);

  const [
    auditoriaDetalle,
    setAuditoriaDetalle,
  ] = useState(null);

  const [
    detalleError,
    setDetalleError,
  ] = useState("");

  /* ==================================================
     FILTROS ACTUALES

     IMPORTANTE:
     En Danlu NO sumamos un día a "hasta".
     El backend ya interpreta hasta como:
     23:59:59.999 GMT-3
  ================================================== */

  const obtenerFiltrosActuales =
    () => ({
      desde:
        clean(desde),

      hasta:
        clean(hasta),

      usuario:
        clean(usuario),

      modulo:
        clean(modulo)
          .toUpperCase(),

      accion:
        clean(accion)
          .toUpperCase(),

      entidad:
        clean(entidad)
          .toUpperCase(),

      resultado:
        clean(resultado)
          .toUpperCase(),

      q:
        clean(q),
    });

  /* ==================================================
     CARGA
  ================================================== */

  const cargarAuditorias =
    async ({
      pagina = 1,
      limite = limit,
      filtros =
        obtenerFiltrosActuales(),
    } = {}) => {
      setLoading(true);
      setError("");

      try {
        const resp =
          await getAuditorias({
            page: pagina,
            limit: limite,
            ...filtros,
          });

        setAuditorias(
          Array.isArray(
            resp?.auditorias
          )
            ? resp.auditorias
            : []
        );

        setTotal(
          Number(
            resp?.total || 0
          )
        );

        setResumen({
          total: Number(
            resp?.resumen
              ?.total ??
              resp?.total ??
              0
          ),

          altas: Number(
            resp?.resumen
              ?.altas || 0
          ),

          modificaciones:
            Number(
              resp?.resumen
                ?.modificaciones ||
                0
            ),

          anulaciones:
            Number(
              resp?.resumen
                ?.anulaciones ||
                0
            ),

          eliminaciones:
            Number(
              resp?.resumen
                ?.eliminaciones ||
                0
            ),

          errores: Number(
            resp?.resumen
              ?.errores || 0
          ),
        });

        setUsuariosDisponibles(
          Array.isArray(
            resp?.usuarios
          )
            ? resp.usuarios
            : []
        );

        setPage(
          Number(
            resp?.page ||
              pagina
          )
        );

        setLimit(
          Number(
            resp?.limit ||
              limite
          )
        );
      } catch (err) {
        console.error(
          "Error cargando auditoría:",
          err
        );

        setError(
          err?.response?.data
            ?.error ||
            err?.response?.data
              ?.message ||
            err?.message ||
            "No se pudo consultar la auditoría."
        );

        setAuditorias([]);
        setTotal(0);

        setResumen({
          total: 0,
          altas: 0,
          modificaciones: 0,
          anulaciones: 0,
          eliminaciones: 0,
          errores: 0,
        });
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    cargarAuditorias({
      pagina: 1,
      limite: 50,
    });

    // eslint-disable-next-line
  }, []);

  /* ==================================================
     DATOS DERIVADOS
  ================================================== */

  const totalPaginas =
    Math.max(
      1,
      Math.ceil(
        total / limit
      )
    );

  const desdeRegistro =
    total === 0
      ? 0
      : (page - 1) *
          limit +
        1;

  const hastaRegistro =
    Math.min(
      page * limit,
      total
    );

  const modulosDisponibles =
    useMemo(() => {
      return Array.from(
        new Set(
          auditorias
            .map((item) =>
              clean(
                item.modulo
              )
            )
            .filter(Boolean)
        )
      ).sort((a, b) =>
        a.localeCompare(
          b,
          "es"
        )
      );
    }, [auditorias]);

  const entidadesDisponibles =
    useMemo(() => {
      return Array.from(
        new Set(
          auditorias
            .map((item) =>
              clean(
                item.entidad
              )
            )
            .filter(Boolean)
        )
      ).sort((a, b) =>
        a.localeCompare(
          b,
          "es"
        )
      );
    }, [auditorias]);

  const paginasVisibles =
    useMemo(() => {
      const paginas = [];

      const inicio =
        Math.max(
          1,
          page - 2
        );

      const fin =
        Math.min(
          totalPaginas,
          page + 2
        );

      for (
        let i = inicio;
        i <= fin;
        i += 1
      ) {
        paginas.push(i);
      }

      return paginas;
    }, [
      page,
      totalPaginas,
    ]);

  const usuarioSeleccionado =
    useMemo(() => {
      return usuariosDisponibles.find(
        (item) =>
          String(item.id) ===
          String(usuario)
      );
    }, [
      usuariosDisponibles,
      usuario,
    ]);

  /* ==================================================
     ACCIONES UI
  ================================================== */

  const buscar = () => {
    cargarAuditorias({
      pagina: 1,
    });
  };

  const limpiarFiltros =
    () => {
      const nuevosFiltros = {
        desde:
          firstDayMonthISO(),

        hasta:
          todayLocalISO(),

        usuario: "",
        modulo: "",
        accion: "",
        entidad: "",
        resultado: "",
        q: "",
      };

      setDesde(
        nuevosFiltros.desde
      );

      setHasta(
        nuevosFiltros.hasta
      );

      setUsuario("");
      setModulo("");
      setAccion("");
      setEntidad("");
      setResultado("");
      setQ("");

      cargarAuditorias({
        pagina: 1,
        filtros:
          nuevosFiltros,
      });
    };

  const cambiarPagina = (
    nuevaPagina
  ) => {
    if (
      nuevaPagina < 1 ||
      nuevaPagina >
        totalPaginas ||
      nuevaPagina === page
    ) {
      return;
    }

    cargarAuditorias({
      pagina:
        nuevaPagina,
    });
  };

  const cambiarLimite = (
    value
  ) => {
    const nuevoLimite =
      Number(
        value || 50
      );

    setLimit(
      nuevoLimite
    );

    cargarAuditorias({
      pagina: 1,
      limite:
        nuevoLimite,
    });
  };

  /* ==================================================
     DETALLE
  ================================================== */

  const abrirDetalle =
    async (id) => {
      setModalVisible(true);

      setDetalleLoading(
        true
      );

      setDetalleError("");

      setAuditoriaDetalle(
        null
      );

      try {
        const resp =
          await getAuditoria(
            id
          );

        setAuditoriaDetalle(
          resp?.auditoria ||
            null
        );
      } catch (err) {
        console.error(
          "Error cargando detalle:",
          err
        );

        setDetalleError(
          err?.response?.data
            ?.error ||
            err?.response?.data
              ?.message ||
            err?.message ||
            "No se pudo consultar el detalle."
        );
      } finally {
        setDetalleLoading(
          false
        );
      }
    };

  const cerrarDetalle =
    () => {
      setModalVisible(
        false
      );

      setAuditoriaDetalle(
        null
      );

      setDetalleError("");
    };

  const cambiosDetalle =
    Array.isArray(
      auditoriaDetalle
        ?.cambios
    )
      ? auditoriaDetalle
          .cambios
      : [];

  /* ==================================================
     TODOS LOS REGISTROS PARA EXPORTACIÓN
  ================================================== */

  const obtenerTodosLosRegistros =
    async () => {
      const filtros =
        obtenerFiltrosActuales();

      let paginaActual =
        1;

      let registros = [];

      let totalRegistros =
        0;

      do {
        const resp =
          await getAuditorias({
            page:
              paginaActual,

            limit: 200,

            ...filtros,
          });

        const lote =
          Array.isArray(
            resp?.auditorias
          )
            ? resp.auditorias
            : [];

        registros =
          registros.concat(
            lote
          );

        totalRegistros =
          Number(
            resp?.total ||
              lote.length ||
              0
          );

        paginaActual +=
          1;
      } while (
        registros.length <
        totalRegistros
      );

      return registros;
    };

  /* ==================================================
     CSV
  ================================================== */

  const exportarCSV =
    async () => {
      setExportando(true);

      setError("");

      try {
        const registros =
          await obtenerTodosLosRegistros();

        const encabezados = [
          "Fecha",
          "Usuario",
          "Email",
          "Rol",
          "Modulo",
          "Entidad",
          "Accion",
          "Referencia",
          "Metodo",
          "Ruta",
          "Resultado",
          "Mensaje",
          "IP",
          "Cambios",
        ];

        const filas =
          registros.map(
            (item) => {
              const cambios =
                Array.isArray(
                  item.cambios
                )
                  ? item.cambios
                      .map(
                        (
                          cambio
                        ) =>
                          `${cambio.campo}: ${mostrarValor(
                            cambio.anterior
                          )} -> ${mostrarValor(
                            cambio.nuevo
                          )}`
                      )
                      .join(
                        " | "
                      )
                  : "";

              return [
                formatDateTime(
                  item.fecha
                ),

                item.usuarioNombre ||
                  "",

                item.usuarioEmail ||
                  "",

                rolTexto(
                  item.usuarioRole
                ),

                item.modulo ||
                  "",

                item.entidad ||
                  "",

                item.accion ||
                  "",

                item.referencia ||
                  "",

                item.metodo ||
                  "",

                item.ruta ||
                  "",

                item.resultado ||
                  "",

                item.mensaje ||
                  "",

                item.ip || "",

                cambios,
              ];
            }
          );

        const contenido = [
          encabezados
            .map(
              escaparCSV
            )
            .join(";"),

          ...filas.map(
            (fila) =>
              fila
                .map(
                  escaparCSV
                )
                .join(";")
          ),
        ].join("\n");

        const blob =
          new Blob(
            [
              `\uFEFF${contenido}`,
            ],
            {
              type:
                "text/csv;charset=utf-8;",
            }
          );

        const url =
          URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href = url;

        link.download =
          `auditoria-${
            desde ||
            "inicio"
          }-${
            hasta ||
            "actual"
          }.csv`;

        document.body
          .appendChild(
            link
          );

        link.click();

        document.body
          .removeChild(
            link
          );

        URL.revokeObjectURL(
          url
        );
      } catch (err) {
        console.error(
          "Error exportando CSV:",
          err
        );

        setError(
          err?.response?.data
            ?.error ||
            err?.message ||
            "No se pudo exportar el CSV."
        );
      } finally {
        setExportando(
          false
        );
      }
    };

  /* ==================================================
     PDF
  ================================================== */

  const exportarPDF =
    async () => {
      setExportando(true);

      setError("");

      try {
        const registros =
          await obtenerTodosLosRegistros();

        const doc =
          new jsPDF(
            "landscape",
            "mm",
            "a4"
          );

        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(
          15
        );

        doc.text(
          "TRANSPORTE DANLU",
          10,
          13
        );

        doc.setFontSize(
          13
        );

        doc.text(
          "Informe de Auditoría",
          10,
          20
        );

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(
          8
        );

        doc.text(
          `Desde: ${
            desde ||
            "Todos"
          }   Hasta: ${
            hasta ||
            "Todos"
          }`,
          10,
          26
        );

        doc.text(
          `Usuario: ${
            usuarioSeleccionado
              ?.usuarioNombre ||
            "Todos"
          } | Módulo: ${
            modulo ||
            "Todos"
          } | Acción: ${
            accion ||
            "Todas"
          }`,
          287,
          20,
          {
            align: "right",
          }
        );

        doc.text(
          `Registros: ${registros.length}`,
          287,
          26,
          {
            align: "right",
          }
        );

        const body =
          registros.map(
            (item) => [
              formatDateTimePdf(
                item.fecha
              ),

              item.usuarioNombre ||
                "-",

              rolTexto(
                item.usuarioRole
              ),

              item.modulo ||
                "-",

              item.entidad ||
                "-",

              item.accion ||
                "-",

              item.referencia ||
                "-",

              item.resultado ||
                "-",

              Array.isArray(
                item.cambios
              )
                ? String(
                    item.cambios
                      .length
                  )
                : "0",
            ]
          );

        autoTable(doc, {
          startY: 31,

          theme: "grid",

          margin: {
            left: 6,
            right: 6,
            bottom: 10,
          },

          head: [
            [
              "Fecha",
              "Usuario",
              "Rol",
              "Modulo",
              "Entidad",
              "Accion",
              "Referencia",
              "Resultado",
              "Cambios",
            ],
          ],

          body,

          styles: {
            fontSize: 6.5,
            cellPadding: 1,
            overflow:
              "linebreak",
            valign:
              "middle",
          },

          headStyles: {
            fillColor: [
              20,
              70,
              150,
            ],

            textColor: 255,

            fontStyle:
              "bold",

            halign:
              "center",
          },

          alternateRowStyles: {
            fillColor: [
              245,
              248,
              252,
            ],
          },

          columnStyles: {
            0: {
              cellWidth: 27,
            },

            1: {
              cellWidth: 35,
            },

            2: {
              cellWidth: 18,
            },

            3: {
              cellWidth: 28,
            },

            4: {
              cellWidth: 28,
            },

            5: {
              cellWidth: 27,
            },

            6: {
              cellWidth: 82,
            },

            7: {
              cellWidth: 20,
            },

            8: {
              cellWidth: 16,
            },
          },

          didDrawPage: (
            data
          ) => {
            const pageHeight =
              doc.internal
                .pageSize
                .getHeight();

            doc.setFontSize(
              7
            );

            doc.setTextColor(
              100
            );

            doc.text(
              `Página ${data.pageNumber}`,
              287,
              pageHeight - 4,
              {
                align:
                  "right",
              }
            );
          },
        });

        doc.save(
          `informe-auditoria-${
            desde ||
            "inicio"
          }-${
            hasta ||
            "actual"
          }.pdf`
        );
      } catch (err) {
        console.error(
          "Error exportando PDF:",
          err
        );

        setError(
          err?.response?.data
            ?.error ||
            err?.message ||
            "No se pudo exportar el PDF."
        );
      } finally {
        setExportando(
          false
        );
      }
    };

  /* ==================================================
     RENDER
  ================================================== */

  return (
    <div className="container-fluid mt-4 mb-4">
      <Card>
        <Card.Body>
          {/* CABECERA */}

          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-0">
                Informe de Auditoría
              </h4>

              <small className="text-muted">
                Consulta detallada de operaciones realizadas en DANLU
              </small>
            </div>

            <div className="mt-2">
              <Button
                variant="outline-secondary"
                size="sm"
                className="mr-2"
                onClick={
                  limpiarFiltros
                }
                disabled={
                  loading ||
                  exportando
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                className="mr-2"
                onClick={buscar}
                disabled={
                  loading ||
                  exportando
                }
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                className="mr-2"
                onClick={
                  exportarCSV
                }
                disabled={
                  loading ||
                  exportando ||
                  total === 0
                }
              >
                CSV
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={
                  exportarPDF
                }
                disabled={
                  loading ||
                  exportando ||
                  total === 0
                }
              >
                {exportando
                  ? "Exportando..."
                  : "PDF"}
              </Button>
            </div>
          </div>

          {/* FILTROS */}

          <Row>
            <Col
              xs={12}
              md={3}
              lg={2}
              className="mb-2"
            >
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                size="sm"
                value={desde}
                onChange={(
                  e
                ) =>
                  setDesde(
                    e.target
                      .value
                  )
                }
              />
            </Col>

            <Col
              xs={12}
              md={3}
              lg={2}
              className="mb-2"
            >
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                size="sm"
                value={hasta}
                onChange={(
                  e
                ) =>
                  setHasta(
                    e.target
                      .value
                  )
                }
              />
            </Col>

            <Col
              xs={12}
              md={6}
              lg={3}
              className="mb-2"
            >
              <Form.Label>
                Usuario
              </Form.Label>

              <Form.Control
                as="select"
                size="sm"
                value={usuario}
                onChange={(
                  e
                ) =>
                  setUsuario(
                    e.target
                      .value
                  )
                }
              >
                <option value="">
                  Todos los usuarios
                </option>

                {usuariosDisponibles.map(
                  (item) => (
                    <option
                      key={
                        item.id
                      }
                      value={
                        item.id
                      }
                    >
                      {item.usuarioNombre ||
                        item.usuarioEmail ||
                        "Usuario"}
                      {" - "}
                      {rolTexto(
                        item.usuarioRole
                      )}

                      {Number(
                        item.cantidadMovimientos ||
                          0
                      ) > 0
                        ? ` (${item.cantidadMovimientos})`
                        : ""}
                    </option>
                  )
                )}
              </Form.Control>
            </Col>

            <Col
              xs={12}
              md={3}
              lg={2}
              className="mb-2"
            >
              <Form.Label>
                Módulo
              </Form.Label>

              <Form.Control
                size="sm"
                value={modulo}
                list="auditoria-modulos"
                placeholder="Ej. MOVIMIENTOS"
                onChange={(
                  e
                ) =>
                  setModulo(
                    e.target
                      .value
                  )
                }
              />

              <datalist id="auditoria-modulos">
                {modulosDisponibles.map(
                  (item) => (
                    <option
                      key={item}
                      value={
                        item
                      }
                    />
                  )
                )}
              </datalist>
            </Col>

            <Col
              xs={12}
              md={3}
              lg={3}
              className="mb-2"
            >
              <Form.Label>
                Acción
              </Form.Label>

              <Form.Control
                as="select"
                size="sm"
                value={accion}
                onChange={(
                  e
                ) =>
                  setAccion(
                    e.target
                      .value
                  )
                }
              >
                <option value="">
                  Todas
                </option>

                <option value="ALTA">
                  ALTA
                </option>

                <option value="MODIFICACION">
                  MODIFICACIÓN
                </option>

                <option value="ANULACION">
                  ANULACIÓN
                </option>

                <option value="ELIMINACION">
                  ELIMINACIÓN
                </option>
              </Form.Control>
            </Col>

            <Col
              xs={12}
              md={4}
              lg={3}
              className="mb-2"
            >
              <Form.Label>
                Entidad
              </Form.Label>

              <Form.Control
                size="sm"
                value={entidad}
                list="auditoria-entidades"
                placeholder="Ej. MOVIMIENTO"
                onChange={(
                  e
                ) =>
                  setEntidad(
                    e.target
                      .value
                  )
                }
              />

              <datalist id="auditoria-entidades">
                {entidadesDisponibles.map(
                  (item) => (
                    <option
                      key={item}
                      value={
                        item
                      }
                    />
                  )
                )}
              </datalist>
            </Col>

            <Col
              xs={12}
              md={4}
              lg={2}
              className="mb-2"
            >
              <Form.Label>
                Resultado
              </Form.Label>

              <Form.Control
                as="select"
                size="sm"
                value={
                  resultado
                }
                onChange={(
                  e
                ) =>
                  setResultado(
                    e.target
                      .value
                  )
                }
              >
                <option value="">
                  Todos
                </option>

                <option value="OK">
                  OK
                </option>

                <option value="ERROR">
                  ERROR
                </option>
              </Form.Control>
            </Col>

            <Col
              xs={12}
              md={8}
              lg={5}
              className="mb-2"
            >
              <Form.Label>
                Buscar
              </Form.Label>

              <Form.Control
                size="sm"
                value={q}
                placeholder="Usuario, email, referencia, registro, mensaje o ruta..."
                onChange={(
                  e
                ) =>
                  setQ(
                    e.target
                      .value
                  )
                }
                onKeyDown={(
                  e
                ) => {
                  if (
                    e.key ===
                    "Enter"
                  ) {
                    buscar();
                  }
                }}
              />
            </Col>

            <Col
              xs={12}
              md={4}
              lg={2}
              className="mb-2"
            >
              <Form.Label>
                Registros por página
              </Form.Label>

              <Form.Control
                as="select"
                size="sm"
                value={limit}
                onChange={(
                  e
                ) =>
                  cambiarLimite(
                    e.target
                      .value
                  )
                }
              >
                <option value={25}>
                  25
                </option>

                <option value={50}>
                  50
                </option>

                <option value={100}>
                  100
                </option>

                <option value={200}>
                  200
                </option>
              </Form.Control>
            </Col>
          </Row>

          {error && (
            <div className="alert alert-danger py-2 mt-2">
              {error}
            </div>
          )}

          {/* RESUMEN */}

          <Row className="mt-3 mb-3">
            <Col
              xs={6}
              md={2}
              className="mb-2"
            >
              <Card className="h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Total movimientos
                  </small>

                  <h5 className="mb-0">
                    {
                      resumen.total
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col
              xs={6}
              md={2}
              className="mb-2"
            >
              <Card className="h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Altas
                  </small>

                  <h5 className="mb-0 text-success">
                    {
                      resumen.altas
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col
              xs={6}
              md={2}
              className="mb-2"
            >
              <Card className="h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Modificaciones
                  </small>

                  <h5 className="mb-0 text-primary">
                    {
                      resumen.modificaciones
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col
              xs={6}
              md={2}
              className="mb-2"
            >
              <Card className="h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Anulaciones
                  </small>

                  <h5 className="mb-0 text-warning">
                    {
                      resumen.anulaciones
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col
              xs={6}
              md={2}
              className="mb-2"
            >
              <Card className="h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Eliminaciones
                  </small>

                  <h5 className="mb-0 text-danger">
                    {
                      resumen.eliminaciones
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col
              xs={6}
              md={2}
              className="mb-2"
            >
              <Card className="h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Errores
                  </small>

                  <h5 className="mb-0 text-danger">
                    {
                      resumen.errores
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* TABLA */}

          <div
            className="table-responsive"
            style={{
              maxHeight:
                "62vh",
            }}
          >
            <Table
              striped
              bordered
              hover
              size="sm"
            >
              <thead
                style={{
                  position:
                    "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor:
                    "#1f5fbf",
                  color:
                    "#ffffff",
                }}
              >
                <tr>
                  <th>
                    Fecha y hora
                  </th>

                  <th>
                    Usuario
                  </th>

                  <th>
                    Rol
                  </th>

                  <th>
                    Módulo
                  </th>

                  <th>
                    Entidad
                  </th>

                  <th>
                    Acción
                  </th>

                  <th>
                    Referencia
                  </th>

                  <th>
                    Método
                  </th>

                  <th>
                    Resultado
                  </th>

                  <th className="text-center">
                    Cambios
                  </th>

                  <th className="text-center">
                    Detalle
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                      />{" "}
                      Cargando auditoría...
                    </td>
                  </tr>
                ) : auditorias.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center text-muted py-4"
                    >
                      No existen movimientos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  auditorias.map(
                    (item) => (
                      <tr
                        key={
                          item._id
                        }
                      >
                        <td
                          style={{
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {formatDateTime(
                            item.fecha
                          )}
                        </td>

                        <td>
                          <div className="font-weight-bold">
                            {item.usuarioNombre ||
                              "Sin usuario"}
                          </div>

                          <small className="text-muted">
                            {item.usuarioEmail ||
                              "-"}
                          </small>
                        </td>

                        <td>
                          {rolTexto(
                            item.usuarioRole
                          )}
                        </td>

                        <td className="font-weight-bold">
                          {item.modulo ||
                            "-"}
                        </td>

                        <td>
                          {item.entidad ||
                            "-"}
                        </td>

                        <td>
                          <Badge
                            variant={accionVariant(
                              item.accion
                            )}
                          >
                            {item.accion ||
                              "-"}
                          </Badge>
                        </td>

                        <td
                          style={{
                            minWidth:
                              220,
                          }}
                        >
                          {item.referencia ||
                            "-"}
                        </td>

                        <td>
                          <Badge
                            variant="secondary"
                          >
                            {item.metodo ||
                              "-"}
                          </Badge>
                        </td>

                        <td>
                          <Badge
                            variant={resultadoVariant(
                              item.resultado
                            )}
                          >
                            {item.resultado ||
                              "-"}
                          </Badge>
                        </td>

                        <td className="text-center font-weight-bold">
                          {Array.isArray(
                            item.cambios
                          )
                            ? item
                                .cambios
                                .length
                            : 0}
                        </td>

                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              abrirDetalle(
                                item._id
                              )
                            }
                          >
                            Ver detalle
                          </Button>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </Table>
          </div>

          {/* PAGINACIÓN */}

          {total > 0 && (
            <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
              <small className="text-muted mb-2">
                Mostrando{" "}
                {
                  desdeRegistro
                }{" "}
                a{" "}
                {
                  hastaRegistro
                }{" "}
                de {total} registros
              </small>

              <Pagination
                size="sm"
              >
                <Pagination.First
                  disabled={
                    page === 1
                  }
                  onClick={() =>
                    cambiarPagina(
                      1
                    )
                  }
                />

                <Pagination.Prev
                  disabled={
                    page === 1
                  }
                  onClick={() =>
                    cambiarPagina(
                      page - 1
                    )
                  }
                />

                {paginasVisibles.map(
                  (
                    numeroPagina
                  ) => (
                    <Pagination.Item
                      key={
                        numeroPagina
                      }
                      active={
                        numeroPagina ===
                        page
                      }
                      onClick={() =>
                        cambiarPagina(
                          numeroPagina
                        )
                      }
                    >
                      {
                        numeroPagina
                      }
                    </Pagination.Item>
                  )
                )}

                <Pagination.Next
                  disabled={
                    page ===
                    totalPaginas
                  }
                  onClick={() =>
                    cambiarPagina(
                      page + 1
                    )
                  }
                />

                <Pagination.Last
                  disabled={
                    page ===
                    totalPaginas
                  }
                  onClick={() =>
                    cambiarPagina(
                      totalPaginas
                    )
                  }
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ==================================================
          MODAL DETALLE
      ================================================== */}

      <Modal
        show={modalVisible}
        onHide={cerrarDetalle}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header
          closeButton
        >
          <Modal.Title>
            Detalle de Auditoría
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {detalleLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />

              <div className="mt-2">
                Cargando detalle...
              </div>
            </div>
          ) : detalleError ? (
            <div className="alert alert-danger">
              {
                detalleError
              }
            </div>
          ) : auditoriaDetalle ? (
            <>
              <Row className="mb-3">
                <Col
                  md={3}
                  className="mb-2"
                >
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Fecha
                      </small>

                      <div className="font-weight-bold">
                        {formatDateTime(
                          auditoriaDetalle.fecha
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col
                  md={3}
                  className="mb-2"
                >
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Usuario
                      </small>

                      <div className="font-weight-bold">
                        {auditoriaDetalle.usuarioNombre ||
                          "-"}
                      </div>

                      <small>
                        {auditoriaDetalle.usuarioEmail ||
                          "-"}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>

                <Col
                  md={2}
                  className="mb-2"
                >
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Rol
                      </small>

                      <div className="font-weight-bold">
                        {rolTexto(
                          auditoriaDetalle.usuarioRole
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col
                  md={2}
                  className="mb-2"
                >
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Acción
                      </small>

                      <div className="mt-1">
                        <Badge
                          variant={accionVariant(
                            auditoriaDetalle.accion
                          )}
                        >
                          {auditoriaDetalle.accion ||
                            "-"}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col
                  md={2}
                  className="mb-2"
                >
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Resultado
                      </small>

                      <div className="mt-1">
                        <Badge
                          variant={resultadoVariant(
                            auditoriaDetalle.resultado
                          )}
                        >
                          {auditoriaDetalle.resultado ||
                            "-"}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mb-3">
                <Card.Body>
                  <Row>
                    <Col
                      md={6}
                      className="mb-3"
                    >
                      <small className="text-muted">
                        Referencia
                      </small>

                      <div className="font-weight-bold">
                        {auditoriaDetalle.referencia ||
                          "-"}
                      </div>
                    </Col>

                    <Col
                      md={3}
                      className="mb-3"
                    >
                      <small className="text-muted">
                        Módulo / Entidad
                      </small>

                      <div>
                        {auditoriaDetalle.modulo ||
                          "-"}
                        {" / "}
                        {auditoriaDetalle.entidad ||
                          "-"}
                      </div>
                    </Col>

                    <Col
                      md={3}
                      className="mb-3"
                    >
                      <small className="text-muted">
                        Método / Ruta
                      </small>

                      <div>
                        {auditoriaDetalle.metodo ||
                          "-"}
                        {" "}
                        {auditoriaDetalle.ruta ||
                          "-"}
                      </div>
                    </Col>

                    <Col
                      md={6}
                      className="mb-3"
                    >
                      <small className="text-muted">
                        Mensaje
                      </small>

                      <div>
                        {auditoriaDetalle.mensaje ||
                          "-"}
                      </div>
                    </Col>

                    <Col
                      md={3}
                      className="mb-3"
                    >
                      <small className="text-muted">
                        IP
                      </small>

                      <div>
                        {auditoriaDetalle.ip ||
                          "-"}
                      </div>
                    </Col>

                    <Col
                      md={3}
                      className="mb-3"
                    >
                      <small className="text-muted">
                        ID Entidad
                      </small>

                      <div
                        style={{
                          wordBreak:
                            "break-all",
                        }}
                      >
                        {auditoriaDetalle.entidadId ||
                          "-"}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <h6>
                Cambios detectados
              </h6>

              <div className="table-responsive mb-3">
                <Table
                  bordered
                  striped
                  size="sm"
                >
                  <thead
                    style={{
                      backgroundColor:
                        "#1f5fbf",
                      color:
                        "#ffffff",
                    }}
                  >
                    <tr>
                      <th>
                        Campo
                      </th>

                      <th>
                        Valor anterior
                      </th>

                      <th>
                        Valor nuevo
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {cambiosDetalle.length ===
                    0 ? (
                      <tr>
                        <td
                          colSpan="3"
                          className="text-center text-muted py-3"
                        >
                          No se registraron cambios individuales.
                        </td>
                      </tr>
                    ) : (
                      cambiosDetalle.map(
                        (
                          cambio,
                          index
                        ) => (
                          <tr
                            key={`${cambio.campo}-${index}`}
                          >
                            <td className="font-weight-bold">
                              {cambio.campo ||
                                "-"}
                            </td>

                            <td
                              className="text-danger"
                              style={{
                                wordBreak:
                                  "break-word",
                              }}
                            >
                              {mostrarValor(
                                cambio.anterior
                              )}
                            </td>

                            <td
                              className="text-success"
                              style={{
                                wordBreak:
                                  "break-word",
                              }}
                            >
                              {mostrarValor(
                                cambio.nuevo
                              )}
                            </td>
                          </tr>
                        )
                      )
                    )}
                  </tbody>
                </Table>
              </div>

              <Row>
                <Col
                  lg={6}
                  className="mb-3"
                >
                  <h6>
                    Datos anteriores
                  </h6>

                  <pre
                    className="border rounded p-3"
                    style={{
                      maxHeight:
                        320,
                      overflow:
                        "auto",
                      fontSize:
                        "0.78rem",
                      background:
                        "#f8f9fa",
                    }}
                  >
                    {jsonBonito(
                      auditoriaDetalle.datosAntes
                    )}
                  </pre>
                </Col>

                <Col
                  lg={6}
                  className="mb-3"
                >
                  <h6>
                    Datos posteriores
                  </h6>

                  <pre
                    className="border rounded p-3"
                    style={{
                      maxHeight:
                        320,
                      overflow:
                        "auto",
                      fontSize:
                        "0.78rem",
                      background:
                        "#f8f9fa",
                    }}
                  >
                    {jsonBonito(
                      auditoriaDetalle.datosDespues
                    )}
                  </pre>
                </Col>

                <Col
                  lg={6}
                  className="mb-3"
                >
                  <h6>
                    Metadata
                  </h6>

                  <pre
                    className="border rounded p-3"
                    style={{
                      maxHeight:
                        280,
                      overflow:
                        "auto",
                      fontSize:
                        "0.78rem",
                      background:
                        "#f8f9fa",
                    }}
                  >
                    {jsonBonito(
                      auditoriaDetalle.metadata
                    )}
                  </pre>
                </Col>

                <Col
                  lg={6}
                  className="mb-3"
                >
                  <h6>
                    Navegador
                  </h6>

                  <div
                    className="border rounded p-3"
                    style={{
                      minHeight:
                        90,
                      wordBreak:
                        "break-word",
                      background:
                        "#f8f9fa",
                    }}
                  >
                    {auditoriaDetalle.userAgent ||
                      "-"}
                  </div>
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={
              cerrarDetalle
            }
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InformeAuditoria;